/**
 * Launch handler consumer. The manifest says `launch_handler: { client_mode: 'focus-existing' }`, so a
 * shortcut ("New game", "Runs") or a captured link, opened while the app is running, only focuses the
 * open window and hands the target URL to `window.launchQueue`. Nothing navigates unless we do.
 *
 * `installLaunchQueue()` runs once at startup and keeps the latest target. The signed-in shell calls
 * `attachLaunchSink()` once its router exists and gets the target then, so a launch that arrives while
 * the install gate or Welcome is showing waits instead of landing on a screen with no router.
 * Browsers without launch_handler never call the consumer and nothing here does anything.
 */

interface LaunchParams {
  targetURL?: string;
}

interface LaunchQueueLike {
  setConsumer(fn: (params: LaunchParams) => void): void;
}

type Sink = (path: string) => void;

let pending: string | null = null;
let sink: Sink | null = null;
let installed = false;

/**
 * The in-app path of a launch target: same origin only, with the `src=pwa` flag from the manifest
 * URLs removed (displayMode.ts strips it on a cold start; here it would end up in the address bar).
 */
export function pathFromLaunch(targetURL: string | undefined, origin: string): string | null {
  if (!targetURL) return null;
  let u: URL;
  try {
    u = new URL(targetURL, origin);
  } catch {
    return null;
  }
  if (u.origin !== origin) return null;
  u.searchParams.delete('src');
  return u.pathname + u.search + u.hash;
}

function deliver(): void {
  if (!sink || pending === null) return;
  const path = pending;
  pending = null;
  sink(path);
}

export function installLaunchQueue(win: { launchQueue?: LaunchQueueLike; location: Location } = window): void {
  if (installed) return;
  const q = win.launchQueue;
  if (!q || typeof q.setConsumer !== 'function') return;
  installed = true;
  q.setConsumer((params) => {
    const path = pathFromLaunch(params.targetURL, win.location.origin);
    if (path === null) return;
    pending = path;
    deliver();
  });
}

/** Hand launches to `fn` (the router) from now on, including one that arrived earlier. Returns the detach function. */
export function attachLaunchSink(fn: Sink): () => void {
  sink = fn;
  deliver();
  return () => {
    if (sink === fn) sink = null;
  };
}

/** Tests only. */
export function resetLaunchQueueForTests(): void {
  pending = null;
  sink = null;
  installed = false;
}
